import BulkEditableSetting from "components/owner-app/BulkEditableSetting";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { BbotButton, BbotInput, BbotSelect, Card, Checkbox } from "top-component-library";
import { CATERING_FULFILLMENT_METHODS } from "Constants";
import { isFeatureEnabledById } from "util/Utils";

// Fallback constraints if the backend hasn't deployed yet or field is absent.
const DEFAULT_LEAD_TIME_HOURS_CONSTRAINTS = { min: 1, max: 168, default: 24 };

const clampLeadTimeHours = (val, constraints = DEFAULT_LEAD_TIME_HOURS_CONSTRAINTS) =>
  val === "" || typeof val !== "number" || Number.isNaN(val)
    ? constraints.default
    : Math.min(constraints.max, Math.max(constraints.min, val));

// Fulfillment methods that support the LOF (Live Order Feed) toggle labels.
// patron_choice is included because it can include delivery as an option.
const LOF_ELIGIBLE_FULFILLMENT_METHODS = ["patron_choice", "driver_delivery", "driver_delivery_with_expo"];

const DEFAULT_FM_TOGGLE_LABELS = [
  {
    fulfillment_method: "driver_delivery",
    button_name: "Delivery",
  },
  {
    fulfillment_method: "patron_pickup",
    button_name: "Carryout",
  },
];

// Defaults for catering merchants switching to patron_choice — surfaced as
// pickup/delivery toggle buttons at checkout. We branch on the prior fulfillment
// method so non-catering merchants don't get catering options pre-populated.
const DEFAULT_CATERING_FM_TOGGLE_LABELS = [
  {
    fulfillment_method: "catering_pickup",
    button_name: "Catering - Pickup",
  },
  {
    fulfillment_method: "catering_delivery",
    button_name: "Catering - Delivery",
  },
];

const BULK_EDIT_BUTTON_TEXT = "Edit For Selected Locations";

const FulfillmentTab = (props) => {
  const [settingsState, setSettingsState] = useState({});
  const [isBulkEditFMLabelInputsVisible, setIsBulkEditFMLabelInputsVisible] = useState(false);

  const {
    bulkEditMode,
    updateLocationSettings,
    updateCustomMenuProperties,
    updatedLocationSettings,
    removeChanges,
    removeCustomMenuChanges,
    defaultLocationSettings,
    fulfillmentMethods,
    selectedLocations,
    selectedCustomer,
  } = props;

  useEffect(() => {
    setSettingsState((prev) => ({
      ...prev,
      isMessageWhenOffBulkEditVisible: !props.bulkEditMode,
    }));
  }, [props.bulkEditMode]);

  const leadTimeConstraints = useMemo(
    () => ({ ...DEFAULT_LEAD_TIME_HOURS_CONSTRAINTS, ...defaultLocationSettings.catering_lead_time_constraints }),
    [defaultLocationSettings.catering_lead_time_constraints]
  );

  useEffect(() => {
    setSettingsState((settingsState) => ({
      ...settingsState,
      allowOrderAhead: props.defaultLocationSettings.allow_order_ahead || "auto",
      fulfillmentMethod: props.defaultLocationSettings.fulfillment_method,
      messageWhenOff: props.defaultLocationSettings.message_when_off || "",
      messageWhenOffEnabled: props.defaultLocationSettings.message_when_off?.length > 0 || false,
      isCheckoutEnabled: props.defaultLocationSettings.enable_checkout,
      forceAllowOrderAhead: props.forceAllowOrderAhead ?? false,
      doordashLof: props.doordashLof ?? false,
      possibleFulfillmentMethods: props.defaultLocationSettings.possible_fulfillment_methods || [],
      integrations: props.defaultLocationSettings.integrations || {},
      cateringLeadTimeHours:
        props.defaultLocationSettings.custom_menu_properties?.catering_lead_time_hours ?? leadTimeConstraints.default,
    }));
  }, [props.defaultLocationSettings, props.forceAllowOrderAhead, props.doordashLof, leadTimeConstraints]);

  // In single-edit mode, check the current fulfillment method.
  // In bulk-edit mode, check the pending bulk change first; if none, require ALL selected
  // locations to match — this avoids showing catering settings when locations are mixed.
  const showIfFulfillmentMethod = (matchFn) =>
    (!bulkEditMode && matchFn(settingsState.fulfillmentMethod)) ||
    (bulkEditMode &&
      (matchFn(updatedLocationSettings.properties?.fulfillment_method) ||
        (!updatedLocationSettings.properties?.fulfillment_method &&
          selectedLocations.every((l) => matchFn(l.fulfillment_method)))));

  const showFMToggleLabelSetting = showIfFulfillmentMethod((m) => m === "patron_choice");

  const showLOFToggleLabelSetting =
    settingsState.doordashLof && showIfFulfillmentMethod((m) => LOF_ELIGIBLE_FULFILLMENT_METHODS.includes(m));

  // Lead time is the V2 draft "lock date" input — only meaningful when the
  // merchant has catering_v2 enabled. V1 merchants on legacy "catering" don't
  // use it (there's no lock date in the immediate-payment flow), so we hide
  // the field to avoid a setting that reads as active but does nothing.
  const showCateringLeadTime =
    isFeatureEnabledById("catering_v2", selectedCustomer) &&
    showIfFulfillmentMethod(
      (m) => m === "catering" || m === "catering_pickup" || m === "catering_delivery"
    );

  const defaultLeadTimeHours =
    defaultLocationSettings.custom_menu_properties?.catering_lead_time_hours ?? leadTimeConstraints.default;

  const updatePatronsChoiceOption = (index, updates) => {
    const updated = settingsState.possibleFulfillmentMethods.map((item, i) =>
      i === index ? { ...item, ...updates } : item
    );
    setSettingsState((prev) => ({ ...prev, possibleFulfillmentMethods: updated }));
    updateLocationSettings({ possible_fulfillment_methods: updated });
  };

  const fulfillmentMethodOptions = Object.entries(fulfillmentMethods).map(([value, label]) => ({ value, label }));

  return (
    <div>
      <div className={"margin-bottom-2"}>
        <h4>Fulfillment Method/Ordering Settings</h4>
        <p>
          Edit ordering settings, including what fulfillment method is used at the selected locations.
          <a href={"https://dd1psupport.zendesk.com/hc/en-us/articles/32479387855380-Editing-Locations-Fulfillment"}>
            {" "}
            More info here.
          </a>
        </p>
      </div>

      <Card>
        <BulkEditableSetting
          title={"Fulfillment Method"}
          description={
            <span>
              This controls the fulfillment method for the selected locations. Learn more about fulfillment methods{" "}
              <a href={"https://central.bbot.menu/article/521-fulfillment-methods"}>here</a>.
            </span>
          }
          inBulkEditMode={bulkEditMode}
          bulkEditButtonText={BULK_EDIT_BUTTON_TEXT}
          onBulkEditInitial={() =>
            updateLocationSettings({
              fulfillment_method: defaultLocationSettings.fulfillment_method,
            })
          }
          onBulkEditCancel={() => removeChanges("fulfillment_method")}
        >
          <FulfillmentSelect
            value={settingsState.fulfillmentMethod}
            onChange={(fm) => {
              const isPatronChoice = fm === "patron_choice";
              const wasCatering = CATERING_FULFILLMENT_METHODS.includes(settingsState.fulfillmentMethod);
              const methods = isPatronChoice
                ? wasCatering
                  ? DEFAULT_CATERING_FM_TOGGLE_LABELS
                  : DEFAULT_FM_TOGGLE_LABELS
                : [];
              setSettingsState({
                ...settingsState,
                fulfillmentMethod: fm,
                possibleFulfillmentMethods: methods,
              });
              updateLocationSettings({
                fulfillment_method: fm,
                possible_fulfillment_methods: methods,
              });
            }}
            options={fulfillmentMethodOptions}
          />
        </BulkEditableSetting>

        {showFMToggleLabelSetting && (
          <BulkEditableSetting
            title={"Patron Choice Options"}
            description={
              <span>
                For <b>Patron's Choice</b> fulfillment method only. This setting controls the fulfillment method and
                label for the fulfillment method toggle on the checkout screen. Most common setup is Pickup/Delivery.
              </span>
            }
            inBulkEditMode={bulkEditMode}
            bulkEditButtonText={BULK_EDIT_BUTTON_TEXT}
            onBulkEditInitial={() => {
              const defaultFMLabels =
                settingsState.possibleFulfillmentMethods.length > 0
                  ? settingsState.possibleFulfillmentMethods
                  : DEFAULT_FM_TOGGLE_LABELS;
              setIsBulkEditFMLabelInputsVisible(true);
              updateLocationSettings({
                possible_fulfillment_methods: defaultFMLabels,
              });
              setSettingsState({
                ...settingsState,
                possibleFulfillmentMethods: defaultFMLabels,
              });
            }}
            onBulkEditCancel={() => {
              setIsBulkEditFMLabelInputsVisible(false);
              removeChanges("possible_fulfillment_methods");
            }}
          >
            {(!bulkEditMode || (bulkEditMode && isBulkEditFMLabelInputsVisible)) && (
              <div className={"padding-x-2 margin-bottom-2"}>
                {settingsState.possibleFulfillmentMethods?.map((possibleFMN, index) => (
                  <div key={`patrons-choice-option-${possibleFMN.fulfillment_method}`}>
                    <PatronsChoiceOption>
                      <span className={"margin-right-2"}>
                        <div className={"text-small-detail"}>Fulfillment Method</div>

                        <BbotSelect
                          defaultValue={possibleFMN.fulfillment_method}
                          onChange={(fulfillmentMethod) => {
                            updatePatronsChoiceOption(index, { fulfillment_method: fulfillmentMethod });
                          }}
                          options={fulfillmentMethodOptions.filter((o) => o.value !== "patron_choice")}
                        />
                      </span>
                      <span className={"margin-right-2"}>
                        <div className={"text-small-detail"}>Button Label</div>
                        <div>
                          <BbotInput
                            value={possibleFMN.button_name}
                            name={"locationName"}
                            onChange={(e) => {
                              updatePatronsChoiceOption(index, { button_name: e.target.value });
                            }}
                          />
                        </div>
                      </span>
                      {index >= 2 && (
                        <BbotButton
                          danger
                          onClick={() => {
                            setSettingsState((settingsState) => {
                              const otherFulfillmentMethods = settingsState.possibleFulfillmentMethods.filter(
                                (_, possibleFMIndex) => index !== possibleFMIndex
                              );
                              updateLocationSettings({
                                possible_fulfillment_methods: otherFulfillmentMethods,
                              });
                              return {
                                ...settingsState,
                                possibleFulfillmentMethods: otherFulfillmentMethods,
                              };
                            });
                          }}
                        >
                          Delete
                        </BbotButton>
                      )}
                    </PatronsChoiceOption>
                    <hr />
                  </div>
                ))}
              </div>
            )}
          </BulkEditableSetting>
        )}

        {showLOFToggleLabelSetting && (
          <BulkEditableSetting
            title={"Driver Delivery Options"}
            description={
              <span>
                For delivery fulfillment methods only, at restaurants with a Doordash Drive integration that use Large
                Order Fulfillment
              </span>
            }
            inBulkEditMode={bulkEditMode}
            bulkEditButtonText={BULK_EDIT_BUTTON_TEXT}
            onBulkEditInitial={() => {}}
            onBulkEditCancel={() => {
              removeChanges("integrations_table_mapping");
            }}
          >
            <div className={"padding-x-2 margin-bottom-2"}>
              <Checkbox
                checked={
                  bulkEditMode
                    ? selectedLocations.every(
                        (location) => location.integrations.doordash_drive?.large_order_fulfillment === true
                      )
                    : settingsState.integrations.doordash_drive?.large_order_fulfillment === true
                }
                onChange={(value) => {
                  let integrations = settingsState.integrations;
                  if (value.target.checked) {
                    _.set(integrations, "doordash_drive.large_order_fulfillment", value.target.checked);
                  } else if (_.includes(Object.keys(integrations), "doordash_drive")) {
                    if (Object.keys(integrations.doordash_drive).length === 1) {
                      _.set(integrations, "doordash_drive", "remove");
                    } else {
                      delete integrations.doordash_drive.large_order_fulfillment;
                    }
                  }
                  setSettingsState({
                    ...settingsState,
                    integrations: integrations,
                  });
                  updateLocationSettings({
                    integrations_table_mapping: integrations,
                  });
                }}
              >
                Location is used for Doordash LOF (Large Order Fulfillment) Orders
              </Checkbox>
            </div>
          </BulkEditableSetting>
        )}

        {showCateringLeadTime && (
          <BulkEditableSetting
            title={"Catering Lead Time (Hours)"}
            description={
              "Number of hours before the fulfillment date that the order locks and payment is charged. " +
              "Customers can edit their draft order until this cutoff. Default is 24 hours."
            }
            inBulkEditMode={bulkEditMode}
            bulkEditButtonText={BULK_EDIT_BUTTON_TEXT}
            onBulkEditInitial={() => {
              const hours = settingsState.cateringLeadTimeHours;
              updateCustomMenuProperties({
                catering_lead_time_hours: hours,
              });
            }}
            onBulkEditCancel={() => {
              setSettingsState((prev) => ({
                ...prev,
                cateringLeadTimeHours: defaultLeadTimeHours,
              }));
              removeCustomMenuChanges(["catering_lead_time_hours"]);
            }}
          >
            <BbotInput
              type="number"
              min={leadTimeConstraints.min}
              max={leadTimeConstraints.max}
              value={settingsState.cateringLeadTimeHours}
              style={{ width: 120 }}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setSettingsState((prev) => ({ ...prev, cateringLeadTimeHours: "" }));
                  return;
                }
                const num = parseInt(raw, 10);
                if (!Number.isNaN(num)) {
                  setSettingsState((prev) => ({ ...prev, cateringLeadTimeHours: num }));
                }
              }}
              onBlur={() => {
                const hours = clampLeadTimeHours(settingsState.cateringLeadTimeHours, leadTimeConstraints);
                setSettingsState((prev) => ({ ...prev, cateringLeadTimeHours: hours }));
                if (hours !== defaultLeadTimeHours) {
                  updateCustomMenuProperties({ catering_lead_time_hours: hours });
                } else {
                  removeCustomMenuChanges(["catering_lead_time_hours"]);
                }
              }}
            />
          </BulkEditableSetting>
        )}

        <BulkEditableSetting
          title={"Guest Checkout/Cart Enabled"}
          description={
            'Toggling this OFF will hide "Add to Cart" buttons on menu item pages, preventing guests from adding ' +
            "items to their cart. The cart icon will not display when toggled off. This feature is ideal for " +
            "view-only menus."
          }
          inBulkEditMode={bulkEditMode}
          bulkEditButtonText={BULK_EDIT_BUTTON_TEXT}
          onBulkEditInitial={() =>
            updateLocationSettings({
              enable_checkout: settingsState.isCheckoutEnabled,
            })
          }
          onBulkEditCancel={() => removeChanges("enable_checkout")}
          toggleConfig={{
            defaultEnabled: defaultLocationSettings.enable_checkout,
            onClick: (e) => {
              setSettingsState({
                ...settingsState,
                isCheckoutEnabled: e.SWITCH_STATE.enabled,
              });
              updateLocationSettings({
                enable_checkout: e.SWITCH_STATE.enabled,
              });
            },
          }}
        />

        <BulkEditableSetting
          title={"Allow Order Ahead"}
          description={
            "If not set to \"auto\" this will override the 'Allow Order Ahead' checkbox in the 'Menu Timers' page."
          }
          inBulkEditMode={bulkEditMode}
          bulkEditButtonText={BULK_EDIT_BUTTON_TEXT}
          onBulkEditInitial={() =>
            updateLocationSettings({
              allow_order_ahead: settingsState.allowOrderAhead,
            })
          }
          onBulkEditCancel={() => removeChanges("allow_order_ahead")}
          toggleConfig={{
            defaultEnabled: defaultLocationSettings.allow_order_ahead === "auto",
            onClick: (e) => {
              setSettingsState({
                ...settingsState,
                allowOrderAhead: e.SWITCH_STATE.enabled ? "auto" : "off",
              });
              updateLocationSettings({
                allow_order_ahead: e.SWITCH_STATE.enabled ? "auto" : "off",
              });
            },
          }}
        />

        <BulkEditableSetting
          title={"Message When Off"}
          description={
            <span>
              This message will be shown to guests who visit your page when the menu is{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={"https://dd1psupport.zendesk.com/hc/en-us/articles/32479355970068-Editing-Location-Codes"}
              >
                off-hours
              </a>
              .
            </span>
          }
          inBulkEditMode={bulkEditMode}
          bulkEditButtonText={BULK_EDIT_BUTTON_TEXT}
          onBulkEditInitial={() =>
            updateLocationSettings({
              message_when_off: settingsState.messageWhenOff,
            })
          }
          onBulkEditCancel={() => removeChanges("message_when_off")}
          toggleConfig={{
            toggleShouldBlockContent: true,
            defaultEnabled: defaultLocationSettings.landing_page_name !== "",
            onClick: (e) => {
              if (!e.SWITCH_STATE.enabled) {
                setSettingsState({
                  ...settingsState,
                  messageWhenOff: "",
                });
                updateLocationSettings({
                  message_when_off: "",
                });
              }
            },
          }}
        >
          <BbotInput
            value={settingsState.messageWhenOff}
            name={"messageWhenOff"}
            onChange={(e) => {
              setSettingsState({
                ...settingsState,
                messageWhenOff: e.target.value,
              });
              updateLocationSettings({
                message_when_off: e.target.value,
              });
            }}
          />
        </BulkEditableSetting>
      </Card>
    </div>
  );
};

export default FulfillmentTab;
export { clampLeadTimeHours, DEFAULT_LEAD_TIME_HOURS_CONSTRAINTS };

const PatronsChoiceOption = styled.div`
  padding: 12px;
  display: flex;
  align-items: flex-end;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const FulfillmentSelect = styled(BbotSelect)`
  min-width: 250px;
`;
